import { Link } from "react-router-dom";
import { Twitter, Linkedin, Github, Mail, MapPin } from "lucide-react";

function ShieldLogo({ className = "h-9 w-9" }) {
    return (
        <svg viewBox="0 0 40 44" className={className} fill="none" aria-hidden="true">
            <defs>
                <linearGradient id="footerShieldGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0EA5A4" />
                    <stop offset="100%" stopColor="#0F766E" />
                </linearGradient>
            </defs>
            <path
                d="M20 2 L36 8 V20 C36 30 28 38 20 42 C12 38 4 30 4 20 V8 Z"
                fill="url(#footerShieldGrad)"
                stroke="#D4AF37"
                strokeWidth="1.2"
            />
            <path
                d="M11 18 Q15 14 20 18 T29 18 M11 23 Q15 19 20 23 T29 23 M11 28 Q15 24 20 28 T29 28"
                stroke="#D4AF37"
                strokeWidth="1.6"
                strokeLinecap="round"
                fill="none"
            />
        </svg>
    );
}

const COLS = [
    {
        title: "Product",
        links: [
            { label: "Features", to: "/#features" },
            { label: "Use Cases", to: "/#use-cases" },
            { label: "Pricing", to: "/pricing" },
            { label: "Dashboard", to: "/dashboard" },
        ],
    },
    {
        title: "Company",
        links: [
            { label: "About Us", to: "/about" },
            { label: "Contact", to: "/contact" },
            { label: "Careers", to: "/contact" },
            { label: "Blog", to: "/contact" },
        ],
    },
    {
        title: "Resources",
        links: [
            { label: "Help Center", to: "/faq" },
            { label: "FAQ", to: "/faq" },
            { label: "Documentation", to: "/faq" },
            { label: "Status", to: "/contact" },
        ],
    },
    {
        title: "Legal",
        links: [
            { label: "Privacy Policy", to: "/privacy" },
            { label: "Terms of Service", to: "/terms" },
            { label: "Refund Policy", to: "/refund-policy" },
            { label: "Shipping Policy", to: "/shipping-policy" },
            { label: "Cookie Policy", to: "/cookie-policy" },
        ],
    },
];

const SOCIALS = [
    { Icon: Twitter, label: "twitter" },
    { Icon: Linkedin, label: "linkedin" },
    { Icon: Github, label: "github" },
    { Icon: Mail, label: "email" },
];

export default function Footer() {
    return (
        <footer
            className="relative bg-[#050F1E] border-t border-white/5 overflow-hidden"
            data-testid="main-footer"
        >
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-teal-500/40 to-transparent" />
            <div className="container-tight py-20">
                <div className="grid grid-cols-2 md:grid-cols-6 gap-10">
                    <div className="col-span-2">
                        <Link to="/" className="flex items-center gap-2.5">
                            <ShieldLogo />
                            <span className="font-bold text-lg text-white">
                                TenderTrack<span className="gradient-text-teal">AI</span>
                            </span>
                        </Link>
                        <p className="mt-5 text-sm text-slate-400 leading-relaxed max-w-xs">
                            Making tender understanding fast, simple, and intelligent. The
                            AI co-pilot for India&apos;s bid teams.
                        </p>
                        <div className="mt-6 flex items-center gap-2">
                            {SOCIALS.map(({ Icon, label }) => (
                                <a
                                    key={label}
                                    href="#"
                                    aria-label={label}
                                    className="h-9 w-9 rounded-xl border border-white/10 flex items-center justify-center text-slate-400 hover:text-gold hover:border-gold/30 transition-colors"
                                >
                                    <Icon className="h-4 w-4" />
                                </a>
                            ))}
                        </div>
                        <div className="mt-6 space-y-1.5 text-xs text-slate-500">
                            <p className="flex items-center gap-2">
                                <Mail className="h-3 w-3" />
                                support@tendertrackai.com
                            </p>
                            <p className="flex items-center gap-2">
                                <MapPin className="h-3 w-3" />
                                Alwar, Rajasthan, India 301414
                            </p>
                        </div>
                    </div>

                    {COLS.map((col) => (
                        <div key={col.title}>
                            <h4 className="text-[11px] font-bold uppercase tracking-[0.2em] text-gold mb-5">
                                {col.title}
                            </h4>
                            <ul className="space-y-3">
                                {col.links.map((l) => (
                                    <li key={l.label}>
                                        <Link
                                            to={l.to}
                                            className="text-sm text-slate-300 hover:text-white transition-colors"
                                            data-testid={`footer-link-${l.label.toLowerCase().replace(/ /g, "-")}`}
                                        >
                                            {l.label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
                    <p className="text-xs text-slate-500">
                        © 2026 TenderTrackAI Technologies Pvt. Ltd. All rights reserved.
                    </p>
                    <div className="flex items-center gap-5 text-[10px] font-mono uppercase tracking-wider text-slate-500">
                        <span>SOC 2 · Type II</span>
                        <span className="h-3 w-px bg-white/10" />
                        <span>GDPR · DPDP 2023</span>
                        <span className="h-3 w-px bg-white/10" />
                        <span>ISO 27001</span>
                    </div>
                </div>
            </div>
        </footer>
    );
}
